/**
 * Modal — a robust overlay wrapper used by every dialog.
 *
 * What it does:
 *  1. The mobile-ops cards use `transform` / `backdrop-filter`, which make a
 *     `position: fixed` child resolve relative to the CARD, not the viewport —
 *     so dialogs render trapped inside a little box. Rendering through a portal
 *     to `document.body` escapes that containing block → true full-screen.
 *  2. On mobile the panel is a content-height bottom sheet (grows with its
 *     content up to 92vh, then the body scrolls); on `sm:`+ it is centered.
 *
 * Escape and backdrop-click close. History/Back handling is deliberately NOT
 * done here: pushing history entries from a UI component fights the router
 * (its cleanup `history.back()` undoes in-modal navigation). Pages that want
 * device-Back-to-close drive their modals from URL search params instead
 * (see PartyLedgerPage's `openModal`/`closeModal`).
 */
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export function Modal({
  children,
  onClose,
  /** Max-width of the panel. */
  size = 'xl',
}: {
  children: ReactNode;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const maxW =
    size === 'sm' ? 'max-w-md' : size === 'md' ? 'max-w-lg' : size === 'lg' ? 'max-w-2xl' : 'max-w-3xl';

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`flex max-h-[92vh] w-full ${maxW} flex-col overflow-hidden rounded-t-2xl border border-border bg-surface shadow-card sm:max-h-[88vh] sm:rounded-md`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
